import PropTypes from "prop-types";
import { memo } from "react";

// MUI
import { Stack } from "@mui/material";

// Components
import Button from "@Layouts/components/Buttons/Button";

// Icons
import RemoveCircleRounded from "@mui/icons-material/RemoveCircleRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";

// Helpers
import { renderTitle } from "@Layouts/components/Tiers/helpers";

function ActionButton(props) {
	return <Button {...props} />;
}

const MemoActionButton = memo(ActionButton);

const getAreaWidth = ({ mainSection, columns, totalColumns, separation, reduction = false }) => {
	let area = mainSection + columns * totalColumns + separation * 2 * totalColumns;
	if (reduction) {
		area = area - columns / 8;
	}
	return `${area}px`;
};

const useSxProperties = (sxProps = null, totalRows = 1, centerTitleAfterRow = 1) => {
	// Remove top From sx Properties
	if (sxProps && Boolean(totalRows > centerTitleAfterRow)) {
		delete sxProps?.["top"];
	}
	return sxProps;
};

export default function TierRows(props) {
	const {
		children,
		showTitle = false,
		title = "Tier Rows",
		titleProps = { className: "tiered-title", sx: { transform: "rotate(270deg)" } },
		titlePropsContainer = { position: "absolute", ml: -5, mt: -10, bottom: "calc(50% + 5px)" },
		centerTitleAfterRow = 1,
		sxTitlePropsContainer = {
			top: 0,
			left: 0,
		},
		spaces = {
			mainSection: 190,
			columns: 100,
			totalColumns: 1,
			totalRows: 1,
			separation: 5,
		},
		deleteButtonProps = {
			variant: "contained",
			classes: "tier-btn remove-tier-btn",
			bgColor: "var(--gray-blue-color)",
			endIcon: <RemoveCircleRounded />,
		},
		deleteButtonText = "Remove",
		deleteButtonDisabled = false,
		addButtonProps = {
			variant: "contained",
			classes: "tier-btn add-tier-btn",
			bgColor: "var(--primary-color)",
			endIcon: <AddCircleRounded />,
		},
		addButtonText = "Add",
		addButtonDisabled = false,
		rowsAreaProps = {
			ml: "0px",
		},
		buttonsContainerProps = {
			direction: "row",
			justifyContent: "center",
			alignItems: "center",
			spacing: 2,
			mt: 1.7,
		},
		containerProps = {
			position: "relative",
			mt: 1,
		},
		onClickAdd = () => {},
		onClickDelete = () => {},
	} = props;

	const sxPropsContainer = useSxProperties(sxTitlePropsContainer, spaces.totalRows, centerTitleAfterRow);

	return (
		<Stack {...containerProps}>
			{showTitle && (
				<Stack {...titlePropsContainer} sx={sxPropsContainer}>
					{renderTitle(titleProps, title)}
				</Stack>
			)}

			<Stack {...rowsAreaProps}>{children}</Stack>

			<Stack className="simple-transition" mt={0.8} width={getAreaWidth({ ...spaces })}>
				<Stack {...buttonsContainerProps}>
					<MemoActionButton
						{...deleteButtonProps}
						text={deleteButtonText}
						disabled={deleteButtonDisabled}
						onClick={onClickDelete}
					/>
					<MemoActionButton {...addButtonProps} text={addButtonText} disabled={addButtonDisabled} onClick={onClickAdd} />
				</Stack>
			</Stack>
		</Stack>
	);
}
TierRows.propTypes = {
	children: PropTypes.node,
	showTitle: PropTypes.bool,
	title: PropTypes.string,
	titleProps: PropTypes.object,
	titlePropsContainer: PropTypes.object,
	centerTitleAfterRow: PropTypes.number,
	sxTitlePropsContainer: PropTypes.object,
	spaces: PropTypes.object,
	deleteButtonProps: PropTypes.object,
	deleteButtonText: PropTypes.string,
	deleteButtonDisabled: PropTypes.bool,
	addButtonProps: PropTypes.object,
	addButtonText: PropTypes.string,
	addButtonDisabled: PropTypes.bool,
	rowsAreaProps: PropTypes.object,
	buttonsContainerProps: PropTypes.object,
	containerProps: PropTypes.object,
	onClickAdd: PropTypes.func,
	onClickDelete: PropTypes.func,
};
